import React, { useState } from 'react';
import {
    DatePicker,
    Divider,
    Label,
    Link,
    Modal,
    PillButton,
    RadioGroup,
    Row,
    Select,
    Stack,
    Text,
    Title,
} from 'library/ui';
import { client, consoleLogInDev } from 'shared';
import { extractErrorMessage } from 'shared/ApiClient';
import { ShiftProviderUser } from 'models';
import RevealModal from 'parent-portal/jobs/upcoming/BusinessCalendar/ShiftCard/RevealModal';
import { BookResultModal } from './BookResultModal';
import { ClearinghouseRecordUploadField } from './ClearinghouseRecordUploadField';
import { EligibleDatesFields } from './EligibleDatesFields';
import { LabeledField } from './LabeledField';
import { SponsorshipSection } from './SponsorshipSection';
import {
    CLEARINGHOUSE_URL,
    DCF_STATUS_OPTIONS,
    REVIEW_NAMES,
    STATUSES_REQUIRING_EXPIRATION,
    STATUS_REQUIRING_REVIEW,
} from './dcfStatuses';
import { BookResult, VerificationForm } from './models';
import { verificationDateChecks } from './verificationDates';

export default function VerifyEligibilityModal({
    workerId,
    workerName,
    submitVerification,
    onClose,
    submitLabel = 'Book',
    eligibleOutcomeCopy,
    sponsorshipPending = false,
    ocaNumber = '',
}: {
    workerId: number;
    workerName: string;
    submitVerification: (form: VerificationForm) => Promise<BookResult | null>;
    onClose: (result?: BookResult) => void;
    submitLabel?: string;
    eligibleOutcomeCopy?: string;
    sponsorshipPending?: boolean;
    ocaNumber?: string;
}) {
    const [revealOpen, setRevealOpen] = useState(false);
    const [eligible, setEligible] = useState<string | null>(null);
    const [dcfStatus, setDcfStatus] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [initiateReview, setInitiateReview] = useState<string | null>(null);
    const [sponsorScreening, setSponsorScreening] = useState<string | null>(null);
    const [oca, setOca] = useState(ocaNumber);
    const [determinationDate, setDeterminationDate] = useState('');
    const [printsExpirationDate, setPrintsExpirationDate] = useState('');
    const [clearinghouseRecord, setClearinghouseRecord] = useState<File>();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<BookResult>();

    const { determinationDateInFuture, printsExpirationDateInPast } = verificationDateChecks(
        determinationDate,
        printsExpirationDate,
    );

    const requiresReview = dcfStatus != null && STATUS_REQUIRING_REVIEW.includes(dcfStatus);
    const requiresExpiration = dcfStatus != null && STATUSES_REQUIRING_EXPIRATION.includes(dcfStatus);
    const reviewName = dcfStatus != null ? REVIEW_NAMES[dcfStatus] : undefined;
    const needsNewScreening = eligible === 'no' && (notFound || dcfStatus === 'a new screening is required');
    const sponsorOffered = needsNewScreening && !sponsorshipPending;
    const sponsoring = sponsorOffered && sponsorScreening === 'yes';
    const sponsorAnswerComplete = !sponsorOffered || (sponsorScreening != null && (!sponsoring || oca.trim() !== ''));
    const noFlowComplete =
        eligible === 'no' &&
        sponsorAnswerComplete &&
        (notFound ||
            (dcfStatus != null &&
                (!requiresExpiration || !!printsExpirationDate) &&
                (!requiresReview || initiateReview != null)));
    const yesFlowComplete =
        eligible === 'yes' &&
        !!determinationDate &&
        !!printsExpirationDate &&
        !determinationDateInFuture &&
        !printsExpirationDateInPast;

    function onEligibleChange(value: string) {
        setEligible(value);
        setDcfStatus(null);
        setNotFound(false);
        setInitiateReview(null);
        setSponsorScreening(null);
        setPrintsExpirationDate('');
    }

    function onDcfStatusChange(value: string) {
        setDcfStatus(value);
        setNotFound(false);
        setInitiateReview(null);
        setSponsorScreening(null);
        if (!STATUSES_REQUIRING_EXPIRATION.includes(value)) {
            setPrintsExpirationDate('');
        }
    }

    function onNotFound() {
        setNotFound(true);
        setDcfStatus(null);
        setInitiateReview(null);
        setSponsorScreening(null);
        setPrintsExpirationDate('');
    }

    const [firstName, ...lastNameParts] = workerName.split(' ');
    const provider = {
        id: workerId,
        first_name: firstName,
        last_name: lastNameParts.join(' '),
    } as ShiftProviderUser;

    async function onRevealInfo() {
        try {
            setRevealOpen(true);
            await client(`api/business/reveal-code/`, { method: 'POST', body: { provider: provider.id } });
        } catch (error) {
            setRevealOpen(false);
            alert(extractErrorMessage(error, 'Cannot send SSN reveal code'));
        }
    }

    function book() {
        setLoading(true);
        const form: VerificationForm =
            eligible === 'no'
                ? {
                      eligibility_date: '',
                      expiration_date: requiresExpiration ? printsExpirationDate : '',
                      screening_document: notFound ? undefined : clearinghouseRecord,
                      clearinghouse_status: notFound ? 'Not found' : 'Yes',
                      eligibility_status: notFound ? undefined : dcfStatus ?? undefined,
                      initiated_review: requiresReview && initiateReview === 'yes',
                      sponsor_screening: sponsoring,
                      oca_number: sponsoring ? oca.trim() : undefined,
                  }
                : {
                      eligibility_date: determinationDate,
                      expiration_date: printsExpirationDate,
                      screening_document: clearinghouseRecord,
                      clearinghouse_status: 'Yes',
                      eligibility_status: 'eligible',
                  };
        submitVerification(form)
            .then((bookResult) => {
                if (!bookResult) {
                    onClose();
                } else if (eligible === 'no') {
                    setResult({
                        status: sponsoring ? 'sponsored' : initiateReview === 'yes' ? 'review_initiated' : 'reported',
                    });
                } else {
                    setResult(bookResult);
                }
            })
            .catch(consoleLogInDev)
            .finally(() => setLoading(false));
    }

    if (result) {
        return <BookResultModal workerName={workerName} result={result} onClose={() => onClose(result)} />;
    }

    return (
        <Modal title={`Verify ${workerName}'s Eligibility to Book`} onClose={() => onClose()} width={560}>
            <Stack gap="md">
                <Stack gap="sm">
                    <Title size="h2">1. Check the Clearinghouse</Title>
                    <Text tone="secondary">
                        Use the information below to look up {workerName} in the DCF Background Screening{' '}
                        <Link href={CLEARINGHOUSE_URL}>Clearinghouse</Link>. Confirm that their status shows they are
                        eligible for child care.
                    </Text>
                    <Row>
                        <PillButton variant="secondary" onClick={onRevealInfo}>
                            View Worker Information
                        </PillButton>
                    </Row>
                </Stack>
                <RevealModal open={revealOpen} onClose={() => setRevealOpen(false)} provider={provider} minimal />
                <Divider />
                <Title size="h2">2. Confirm Eligibility</Title>
                <LabeledField
                    label={`Is ${workerName} eligible for DCF Child Care?`}
                    help="On the worker's Clearinghouse record, check that their DCF Child Care status shows they are eligible. If it does not say eligible, they are not eligible for DCF Child Care."
                    image="https://cdn.join-tandem.com/media/tandem_static/child_care_eligibility_instructions.png"
                    required
                >
                    <RadioGroup
                        options={[
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                        ]}
                        value={eligible}
                        onChange={onEligibleChange}
                    />
                </LabeledField>
                {eligible === 'no' && (
                    <Stack gap="md">
                        <LabeledField
                            label="What does DCF Child Care currently say?"
                            help="On the worker's Clearinghouse record, find their DCF Child Care row and select the status shown there."
                            image="https://cdn.join-tandem.com/media/tandem_static/child_care_eligibility_instructions.png"
                            required
                        >
                            <Select
                                options={DCF_STATUS_OPTIONS}
                                value={dcfStatus ?? ''}
                                onChange={onDcfStatusChange}
                                placeholder="Select a status"
                                fullWidth
                            />
                            <Link onClick={onNotFound}>The worker did not come up in Clearinghouse</Link>
                        </LabeledField>
                        {notFound && (
                            <Text tone="secondary">
                                You reported that {workerName} did not come up in the Clearinghouse.
                            </Text>
                        )}
                        {requiresExpiration && (
                            <LabeledField
                                label="Retained Prints Expiration Date"
                                help="Find the Retained Prints Expiration Date on the worker's Clearinghouse record in the top right corner, then input the date."
                                image="https://cdn.join-tandem.com/media/tandem_static/retained_prints_instructions.png"
                                required
                            >
                                <DatePicker value={printsExpirationDate} onChange={setPrintsExpirationDate} fullWidth />
                            </LabeledField>
                        )}
                        {requiresReview && (
                            <Stack gap="sm">
                                <Label>
                                    Do you want to initiate the {reviewName}?
                                    <Text component="span" tone="error">
                                        {' '}
                                        *
                                    </Text>
                                </Label>
                                <RadioGroup
                                    options={[
                                        { value: 'yes', label: 'Yes' },
                                        { value: 'no', label: 'No' },
                                    ]}
                                    value={initiateReview}
                                    onChange={setInitiateReview}
                                />
                                {initiateReview === 'yes' && (
                                    <Stack gap="sm">
                                        <Text tone="secondary">
                                            You can initiate the {reviewName} directly in the{' '}
                                            <Link href={CLEARINGHOUSE_URL}>Clearinghouse</Link>. Once you&rsquo;ve
                                            submitted it there, press &ldquo;I initiated the {reviewName}&rdquo; below
                                            and we&rsquo;ll note it&rsquo;s in process. When the screening comes back as
                                            Eligible, return to Tandem to book the substitute.
                                        </Text>
                                        {dcfStatus === 'resubmission required' && (
                                            <Text tone="secondary">
                                                The Resubmission fee is $12. Tandem will reimburse the $12 fee as a
                                                credit on your next invoice.
                                            </Text>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        )}
                        <SponsorshipSection
                            workerName={workerName}
                            needsNewScreening={needsNewScreening}
                            sponsorshipPending={sponsorshipPending}
                            sponsorScreening={sponsorScreening}
                            onSponsorScreeningChange={setSponsorScreening}
                            oca={oca}
                            onOcaChange={setOca}
                            hadOcaOnFile={!!ocaNumber}
                        />
                        {dcfStatus != null && !notFound && (
                            <ClearinghouseRecordUploadField file={clearinghouseRecord} onChange={setClearinghouseRecord} />
                        )}
                        {noFlowComplete && (
                            <Text tone={sponsoring ? 'secondary' : 'error'}>
                                {sponsoring
                                    ? `You cannot book ${workerName} yet. We'll reach out to them to schedule their fingerprints, and their status will update here as the screening progresses.`
                                    : `You cannot currently book ${workerName}. ${
                                          initiateReview === 'yes'
                                              ? `Only press "I initiated the ${reviewName}" after you've submitted it in the Clearinghouse.`
                                              : `There is no further action available to you yet — press "Submit" to save what you found so our records stay up to date.`
                                      }`}
                            </Text>
                        )}
                    </Stack>
                )}
                {eligible === 'yes' && (
                    <Stack gap="md">
                        <EligibleDatesFields
                            workerName={workerName}
                            determinationDate={determinationDate}
                            onDeterminationDateChange={setDeterminationDate}
                            printsExpirationDate={printsExpirationDate}
                            onPrintsExpirationDateChange={setPrintsExpirationDate}
                        />
                        <ClearinghouseRecordUploadField file={clearinghouseRecord} onChange={setClearinghouseRecord} />
                    </Stack>
                )}
                <Divider />
                {eligible !== 'no' && (
                    <Text tone="secondary">
                        {eligibleOutcomeCopy ? (
                            <>{eligibleOutcomeCopy} </>
                        ) : (
                            <>Once you confirm that {workerName} is eligible, we&rsquo;ll add them to your schedule. </>
                        )}
                        If they go 90 days without working at your center, you&rsquo;ll need to verify their eligibility
                        again.
                    </Text>
                )}
                <Row justify="end" fullWidth>
                    <PillButton disabled={(!yesFlowComplete && !noFlowComplete) || loading} onClick={book}>
                        {loading
                            ? 'Submitting...'
                            : eligible === 'no'
                              ? sponsoring
                                  ? 'Sponsor Screening'
                                  : initiateReview === 'yes'
                                    ? `I initiated the ${reviewName}`
                                    : 'Submit'
                              : submitLabel}
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}
